import { useApolloClient } from '@apollo/client';
import { EmailColumn } from '@starryui10/grids/cells/EmailColumn/EmailColumn';
import { EntryStatusColumn } from '@starryui10/grids/cells/entryStatusColumn/EntryStatusColumn';
import { FlagColumn } from '@starryui10/grids/cells/FlagColumn/FlagColumn';
import { useHeaderCell } from '@starryui10/grids/cells/HeaderCell/HeaderCell';
import { useHoverColumn } from '@starryui10/grids/cells/HoverColumn/HoverColumn';
import { useOptionsColumn } from '@starryui10/grids/cells/OptionsColumn/OptionsColumn';
import { usePhotoColumn } from '@starryui10/grids/cells/photoColumn/PhotoColumn';
import { useSelectionColumn } from '@starryui10/grids/cells/SelectionColumn/SelectionColumn';
import { SituationResponseFlagColumn } from '@starryui10/grids/cells/SituationResponseFlagColumn/SituationResponseFlagColumn';
import { ColDef } from 'ag-grid-community';
import { List } from 'immutable';
import React, { createContext, FC, useContext } from 'react';
import { avatarImagePopup, entryDirectorySorting } from '../../cache';
import EntryOptions from '../../components/EntryOptions';
import { EntrySummaryView, IEntrySummaryViewProps } from '../../components/EntrySummaryView';
import { PhotoPopup } from '../../components/PhotoPopup';
import { useAppContext } from '../../context/AppContext';
import { useEntryDirectoryEvents } from '../../context/EntryDirectoryContext';
import { DirectoryFieldsEnum, IDirectoryNodes } from '../../graphql/useMainDirectoryQuery';
import { useMainDirectoryCache } from '../../graphql/useMainDirectorySelectedQuery';
import { useUpdateMainDirectorySelectedItem } from '../../graphql/useUpdateMainDirectorySelectedItem';
import { ColumnFilterButton, IColumnFilterButtonProps } from '../../components/ColumnFilter/ColumnFilterButton';
import { strings } from '../../localization';
import { useDirectoryDispatch } from '../../state/DirectoryState';

interface IColumnsProviderContextProps {
    columns: List<ColDef>;
}

const ColumnsProviderContext = createContext<IColumnsProviderContextProps>({} as IColumnsProviderContextProps);

export const ColumnsProvider: FC = ({ children }) => {
    const { global, security } = useAppContext();
    const { openEntry } = useEntryDirectoryEvents();
    const { dispatch } = useDirectoryDispatch();
    const { cache } = useApolloClient();
    const { updateSelectedInCache, getSelectedAmountFromCache, updateSelectedAmountInCache } = useMainDirectoryCache(cache);
    const [mutateSelectedItem] = useUpdateMainDirectorySelectedItem();

    const flagColumn = useHoverColumn<IEntrySummaryViewProps>(undefined, security.entry.canView ? EntrySummaryView : undefined, FlagColumn);

    const linkColumn = useHoverColumn<IEntrySummaryViewProps>(
        security.entry.canView ? (data) => openEntry(Number(data.id)) : undefined,
        security.entry.canView ? EntrySummaryView : undefined
    );

    const optionColumn = useOptionsColumn({
        ariaLabel: strings.MainDirectory_Label_FurtherOptions,
        title: strings.MainDirectory_Label_FurtherOptions,
        Component: EntryOptions,
    });
    const photoColumn = usePhotoColumn<IDirectoryNodes>((data) => ({
        src: data?.smallImage?.url,
        initials: data?.nameInitials,
        altText: data?.nameLast,
        onClick: () =>
            avatarImagePopup({
                visible: true,
                data,
            }),
    }));

    const headerCell = useHeaderCell<DirectoryFieldsEnum, IColumnFilterButtonProps>(
        entryDirectorySorting,
        (key, order) => {
            dispatch({ type: 'SORT', key, order });
        },
        ColumnFilterButton
    );

    const selectionCell = useSelectionColumn((selected, data) => {
        let amount = getSelectedAmountFromCache();

        if (selected) {
            amount = amount + 1;
        } else {
            amount = amount - 1;
        }
        updateSelectedAmountInCache(amount);
        updateSelectedInCache(data.id, selected);
        mutateSelectedItem({ variables: { checked: selected, id: data.id } });
    }, alert);

    const columns: ColDef[] = [];

    columns.push(
        {
            field: 'isSelected',
            ...selectionCell,
        },
        {
            headerName: '',
            // hide: true,
            field: 'image.url',
            width: 58,
            ...photoColumn,
        },
        {
            headerName: '',
            width: 30,
            ...optionColumn,
        },
        {
            headerName: '',
            width: 30,
            field: 'displayFlag',
            ...flagColumn,
        },
        {
            headerName: '',
            width: 30,
            sortable: false,
            field: 'situationResponseEnum',
            cellRendererFramework: SituationResponseFlagColumn,
        },
        {
            headerName: 'First Name',
            field: 'nameFirst',
            width: 150,
            ...linkColumn,
            ...headerCell('NAME_FIRST'),
        },
        {
            headerName: 'Last Name',
            field: 'nameLast',
            width: 150,
            ...linkColumn,
            ...headerCell('NAME_LAST'),
        },
        {
            headerName: 'Preferred Name',
            hide: true,
            width: 120,
            field: 'namePreferred',
            ...linkColumn,
            ...headerCell('NAME_PREFERRED'),
        },
        {
            headerName: 'Other Name',
            hide: true,
            width: 120,
            field: 'nameOther',
            ...linkColumn,
            ...headerCell('NAME_OTHER'),
        },
        {
            headerName: 'Email',
            width: 150,
            cellRendererFramework: EmailColumn,
            field: 'email',
            ...headerCell('EMAIL'),
        },
        {
            headerName: 'Room',
            width: 120,
            field: 'roomSpace_Description',
            ...headerCell('ROOM_SPACE_DESCRIPTION'),
        },
        {
            headerName: 'Extension',
            width: 100,
            field: 'extension_Description',
            hide: true,
            ...headerCell('EXTENSION_DESCRIPTION'),
        },
        {
            headerName: 'Virtual Extension',
            width: 100,
            hide: true,
            field: 'extension_Virtual',
            ...headerCell('EXTENSION_VIRTUAL'),
        },
        {
            headerName: 'Entry Status',
            width: 100,
            cellRendererFramework: EntryStatusColumn,
            field: 'entryStatus.value',
            ...headerCell('ENTRY_STATUS_DESCRIPTION'),
        },
        {
            headerName: 'Location',
            width: 100,
            field: 'roomLocation_Description',
            ...headerCell('ROOM_LOCATION_DESCRIPTION'),
        },
        {
            headerName: 'Room Type',
            field: 'roomType_Description',
            width: 100,
            hide: true,
            ...headerCell('ROOM_TYPE_DESCRIPTION'),
        },
        {
            headerName: 'Entry ID',
            field: 'entryID',
            width: 100,
            hide: true,
            ...linkColumn,
            ...headerCell('EVENT_ID'),
        }
    );

    security?.entry?.field?.iD1?.canView &&
        columns.push({
            headerName: global?.setting?.iD1Label,
            width: 60,
            field: 'iD1',
            ...linkColumn,
            ...headerCell('I_D_1'),
        });

    security?.entry?.field?.iD2?.canView &&
        columns.push({
            headerName: global?.setting?.iD2Label,
            field: 'iD2',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_2'),
        });

    security?.entry?.field?.iD3?.canView &&
        columns.push({
            headerName: global?.setting?.iD3Label,
            field: 'iD3',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_3'),
        });

    security?.entry?.field?.iD4?.canView &&
        columns.push({
            headerName: global?.setting?.iD4Label,
            field: 'iD4',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_4'),
        });

    security?.entry?.field?.iD5?.canView &&
        columns.push({
            headerName: global?.setting?.iD5Label,
            field: 'iD5',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_5'),
        });

    columns.push(
        {
            headerName: 'Group',
            field: 'group_Description',
            width: 60,
            ...headerCell('GROUP_DESCRIPTION'),
        },
        {
            headerName: 'Event',
            field: 'event_Description',
            width: 60,
            hide: true,
            ...headerCell('EVENT_DESCRIPTION'),
        },
        {
            headerName: 'Application Status',
            field: 'applicationStatus_Description',
            width: 60,
            hide: true,
            ...headerCell('APPLICATION_STATUS_DESCRIPTION'),
        },
        {
            headerName: 'Category Level 1',
            field: 'level1',
            ...headerCell('LEVEL_1'),
        },
        {
            headerName: 'Category Level 2',
            field: 'level2',
            hide: true,
            ...headerCell('LEVEL_2'),
        },
        {
            headerName: 'Category Level 3',
            field: 'level3',
            hide: true,
            ...headerCell('LEVEL_3'),
        },
        {
            headerName: 'Category Level 4',
            field: 'level4',
            hide: true,
            ...headerCell('LEVEL_4'),
        },
        {
            headerName: 'Category',
            field: 'category_Description',
            hide: true,
            ...headerCell('CATEGORY_DESCRIPTION'),
        },
        {
            headerName: 'Situation Response',
            field: 'situationResponse_Description',
            hide: true,
            ...headerCell('SITUATION_RESPONSE_DESCRIPTION'),
        },
        {
            headerName: 'Gender',
            field: 'genderEnum_Description',
            hide: true,
            ...headerCell('GROUP_DESCRIPTION'),
        },
        {
            headerName: 'Active Term Booking',
            field: 'termSession_Description',
            hide: true,
        },
        {
            headerName: 'Classification',
            field: 'classification_Description',
            hide: true,
            ...headerCell('CATEGORY_DESCRIPTION'),
        }
    );

    return (
        <>
            <ColumnsProviderContext.Provider value={{ columns: List<ColDef>(columns) }}>{children}</ColumnsProviderContext.Provider>
            <PhotoPopup />
        </>
    );
};

export const useColumnsProviderContext = () => useContext(ColumnsProviderContext);
